//============================================================================
// Name        : types.ts
// Purpose     : Define manufacturing/scheduling module types
// Created     : 2019-08-09
// Author      : Senitron Team
// Copyright   : Copyright 2019 Senitron Corporation. All rights reserved.
//============================================================================

//============================================================================
// Type Aliases
//============================================================================

export type ChangeInputEvent    = React.ChangeEvent<HTMLInputElement>;
export type ChangeTextAreaEvent = React.ChangeEvent<HTMLTextAreaElement>;
export type ChangeSelectEvent   = React.ChangeEvent<HTMLSelectElement>;

//============================================================================
// Redux
//============================================================================

export interface IReduxReducers {
    schedule: IScheduleState;
    editSchedule: IEditScheduleState;
}

export interface IScheduleState {
    showAsLbs: boolean;
    showAsCases: boolean;
    showSOInspect: boolean;
    product: IProduct;
    qtyOnHandCases: number;
    qtyOnHandLbs: number;
    storageUnit: IStorageUnit;
    pastDueSlice: IProduction[];
    scheduledSlice: IProduction[];
    scheduledSlice1: IProduction[];
    scheduledSlice2: IProduction[];
    scheduledSlice3: IProduction[];
    scheduledSlice4: IProduction[];
    soPdItems: ISoItem[];
    soItems: ISoItem[];
    soItems1: ISoItem[];
    soItems2: ISoItem[];
    soItems3: ISoItem[];
    soItems4: ISoItem[];

    inspectSO: IInspectSoItemsResp;

    // URLs
    editScheduleUrl: string;
    soItemsUrl?: string;

    // Actions
    storeInitialState?: (props: IScheduleState) => void;
    updateState?: (newState: object) => void;
    showSOInspectDialog?: (soItems: ISoItem[]) => void;
}

export enum ScheduleActions {
    StoreInitialState,
    UpdateState,
    HideSOInspectDialog,
    ShowSOInspectDialog
}

export interface IEditScheduleState {
    showAsLbs: boolean;
    showAsCases: boolean;
    product: IProduct;
    qtyOnHandCases: number;
    qtyOnHandLbs: number;
    schedulerUsers: IUser[];
    productions: IProduction[];
    pastDueProductions: IProduction[];
    startDay: number;
    endDay: number;
    zones: IZone[];
    subzones: IZone[];
    storageUnit: IStorageUnit;

    // ------------------------------------------
    // Dialogs
    showScheduleProdDialog: boolean;
    scheduleDialogUserId: number;
    scheduleDialogUsername: string;

    // Confirmation Dialog
    showConfirmDialog: boolean;
    confirmDialogTitle: string;
    confirmDialogMsg: string;
    confirmDlgPositiveCb: () => void;
    confirmDlgNegativeCb: () => void;

    // ------------------------------------------
    // Actions
    storeInitialState?: (props: IEditScheduleState) => void;
    updateState?: (newState: object) => void;
    showScheduleProduction?: (user: IUser) => void;
}

export enum EditScheduleActions {
    StoreInitialState,
    UpdateState,
    ShowScheduleProduction,
    HideScheduleProduction
}

export interface IScheduleProductionDialog {
    show: boolean;
    product: IProduct;
    startDay: number;
    endDay: number;
    zones: IZone[];
    subzones: [];
    schedulerUsers: IUser[];
    productions: IProduction[];
    pastDueProductions: IProduction[];
    scheduleDialogUserId: number;

    // ------------------------------------------
    // Actions
    shouldCancel?: () => void;
    hideScheduleProduction?: () => void;
}

export interface IConfirmDialog {
    show: boolean;
    title: string;
    message: string;
    positiveLbl: string;
    negativeLbl: string;

    // ------------------------------------------
    // Actions
    positiveCallback?: () => void;
    negativeCallback?: () => void;
}

export interface ISOInspectDialog {
    show: boolean;
    product: IProduct;
    inspectSO: IInspectSoItemsResp;

    // ------------------------------------------
    // Actions
    hide: () => void;
}

//============================================================================
// Module Interfaces
//============================================================================

export interface IProduct {
    id: number;
    alu: string;
    number: string;
    description: string;
}

export interface IStorageUnit {
    id: number;
    item_number: string;
    name: string;
    measure_unit: string;
}

export interface ISearchProductProps {
    searchUrl: string;
    scheduleUrl: string;
}

export interface ISearchProductState {
    query: string;
    products: IProduct[];
}

export interface IProduction {
    id?: number;
    scheduler_name: string;
    scheduler_user_id: number;
    scheduled_date: string;
    produced_date: string;
    production_item: string;
    product_name: string;
    needed_qty: number;
    qty_produced: number;
    pending_qty: number;
    storage_unit_name: string;
    asignee_name: string;
    asignee_user_id: number;
    sub_zone_id: number;
    pallet_count: number;
    pallet_units: number;
    container_units: number;
    container_cost: number;
    unit_cost: number;
    pallet_cost: number;
    total_cost: number;
    new_lot_number: string;
    tag_ids: [];
    status: string;
    created_at: string;
    updated_at: string;
}

export interface IDayQuantities {
    date: string;
    date_qty: number;
}

export interface ISchedulerUser {
    id: number;
    username: string;
    email: string;
    full_name: string;
}

export interface IUser {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
}

export interface IZone {
    id: number;
    name: string;
}

export interface ISoItem {
    id: number;
    mf_shipment_id: number;
    salesorder_id: number;
    item_description: string;
    item_number: string;
    storage_unit: string;
    unit_weight: number;
    measure_unit: string;
    measure_unit_cost: number;
    order_price: number;
    expected_shipment_date: string;
    order_qty: number;
    created_at: string;
    updated_at: string;
    alu: string;
}

export interface ISaleOrder {
    id: number;
    type_id: number;
    salesorder_id: number;
    customer_id: number;
    customer_name: string;
    order_price: number;
    order_qty: number;
    expected_shipment_date: string;
    send_api_time: string;
    created_at: string;
    updated_at: string;
}

export interface IInspectSoItemsResp {
    inspectSO: ISoItem[];
    sos: ISaleOrder[];
}
